<template>
	<div class="loan-comments-tab container-fluid px-0">
		<div
			class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 border-bottom pb-2"
		>
			<h6 class="m-0 fw-bold akkurate-green">Comments</h6>
			<b-button
				type="button"
				class="akkurate-green-btn btn-size text-white text-uppercase"
				:disabled="!effectiveLoanRef"
				@click="openAddModal"
			>
				Add comment
			</b-button>
		</div>

		<div v-if="listError" class="alert alert-danger py-2 small mb-2">
			{{ listError }}
		</div>

		<div
			v-if="listLoading && comments.length === 0"
			class="text-center text-muted py-4"
		>
			<b-spinner small class="align-middle me-2" />
			Loading comments…
		</div>

		<div
			v-else-if="!listLoading && comments.length === 0 && !listError"
			class="text-center text-muted py-4 akkurate-small"
		>
			No comments yet. Use <strong>Add comment</strong> to add one.
		</div>

		<div v-else class="akkurate-small position-relative">
			<div
				v-if="listLoading && comments.length > 0"
				class="small text-muted mb-2"
			>
				<b-spinner small class="align-middle me-1" /> Updating…
			</div>
			<div
				v-for="(item, idx) in comments"
				:key="commentRowKey(item, idx)"
				class="border rounded mb-2 p-2 bg-white"
			>
				<div class="d-flex justify-content-between flex-wrap gap-1 mb-1">
					<span class="fw-semibold text-dark">{{
						formatCommentAuthor(item)
					}}</span>
					<span class="text-muted">{{
						formatCommentDate(item.created_at)
					}}</span>
				</div>
				<p class="mb-0 text-break" style="white-space: pre-wrap">
					{{ item.comment != null ? String(item.comment) : "" }}
				</p>
			</div>

			<div
				v-if="pagination && pagination.last_page > 1"
				class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top"
			>
				<small class="text-muted"
					>Page {{ pagination.current_page }} of
					{{ pagination.last_page }}</small
				>
				<div>
					<b-button
						size="sm"
						variant="outline-secondary"
						class="me-1"
						:disabled="listLoading || pagination.current_page <= 1"
						@click="goPage(pagination.current_page - 1)"
					>
						Previous
					</b-button>
					<b-button
						size="sm"
						variant="outline-secondary"
						:disabled="
							listLoading ||
							pagination.current_page >= pagination.last_page
						"
						@click="goPage(pagination.current_page + 1)"
					>
						Next
					</b-button>
				</div>
			</div>
		</div>

		<b-modal
			:id="modalId"
			title="Add comment"
			@hidden="onAddModalHidden"
		>
			<b-form-textarea
				id="loan-comment-text"
				v-model.trim="draftComment"
				rows="5"
				max-rows="12"
				placeholder="Enter your comment (minimum 3 characters)"
				:disabled="submitLoading"
			/>
			<small class="text-muted d-block mt-1"
				>{{ draftComment.length }} characters (min 3)</small
			>
			<div v-if="submitError" class="alert alert-danger py-2 small mt-2 mb-0">
				{{ submitError }}
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button
						variant="secondary"
						class="me-2"
						:disabled="submitLoading"
						@click="$bvModal.hide(modalId)"
					>
						Cancel
					</b-button>
					<b-button
						class="akkurate-green-btn text-white"
						:disabled="submitLoading || !canSubmitComment"
						@click="submitNewComment"
					>
						<b-spinner v-if="submitLoading" small class="me-1" />
						Submit
					</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { apiErrorMessage } from "@/utils/apiErrorMessage.js";
import { loanCommentsPath } from "@/utils/loanViewApiPaths.js";

const DEFAULT_PER_PAGE = 15;

export default {
	name: "LoanCommentsTab",
	props: {
		/** Numeric id or loan_number — same as Loan View `resolvedLoanRef` */
		loanRef: {
			type: [Number, String],
			default: null,
		},
	},
	data() {
		return {
			comments: [],
			listLoading: false,
			listError: null,
			pagination: null,
			currentPage: 1,
			perPage: DEFAULT_PER_PAGE,
			draftComment: "",
			submitLoading: false,
			submitError: null,
		};
	},
	computed: {
		effectiveLoanRef() {
			if (this.loanRef == null || this.loanRef === "") {
				return null;
			}
			if (typeof this.loanRef === "number") {
				return Number.isFinite(this.loanRef) && this.loanRef > 0
					? this.loanRef
					: null;
			}
			const s = String(this.loanRef).trim();
			return s ? s : null;
		},
		modalId() {
			const key = this.effectiveLoanRef;
			const safe =
				key != null
					? String(key).replace(/[^a-zA-Z0-9_-]/g, "_")
					: "0";
			return `bv-modal-loan-add-comment-${safe}`;
		},
		canSubmitComment() {
			return (this.draftComment || "").trim().length >= 3;
		},
	},
	watch: {
		effectiveLoanRef: {
			immediate: true,
			handler(ref) {
				if (ref != null) {
					this.currentPage = 1;
					this.fetchComments(1);
				} else {
					this.comments = [];
					this.listError = null;
					this.pagination = null;
				}
			},
		},
	},
	methods: {
		commentRowKey(item, idx) {
			if (item && item.id != null) {
				return String(item.id);
			}
			return `c-${idx}-${item?.created_at || ""}`;
		},
		formatCommentAuthor(item) {
			const u = item && item.user;
			if (!u || typeof u !== "object") {
				return "—";
			}
			const fn = u.first_name || "";
			const sn = u.surname || "";
			const name = `${fn} ${sn}`.trim();
			return name || (u.id != null ? `User #${u.id}` : "—");
		},
		formatCommentDate(raw) {
			if (raw == null || raw === "") {
				return "";
			}
			const d = new Date(raw);
			return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
		},
		normalizeListResponse(res) {
			const root = res && res.data;
			if (!root) {
				return { items: [], meta: null };
			}
			if (Array.isArray(root.data)) {
				return {
					items: root.data,
					meta: root.meta || null,
				};
			}
			if (
				root.data &&
				typeof root.data === "object" &&
				Array.isArray(root.data.data)
			) {
				return {
					items: root.data.data,
					meta: root.data.meta || root.meta || null,
				};
			}
			if (Array.isArray(root)) {
				return { items: root, meta: null };
			}
			return { items: [], meta: null };
		},
		async fetchComments(page) {
			const ref = this.effectiveLoanRef;
			if (ref == null) {
				return;
			}
			this.listLoading = true;
			this.listError = null;
			try {
				const response = await ApiService.query(
					loanCommentsPath(ref),
					{
						params: {
							per_page: Math.min(
								100,
								Math.max(1, this.perPage || DEFAULT_PER_PAGE),
							),
							page: page || 1,
						},
					},
				);
				const { items, meta } = this.normalizeListResponse(response);
				this.comments = items;
				this.currentPage = page || 1;
				if (meta && typeof meta === "object") {
					this.pagination = {
						current_page: meta.current_page || this.currentPage,
						last_page: meta.last_page || 1,
						per_page: meta.per_page,
						total: meta.total,
					};
				} else {
					this.pagination = null;
				}
			} catch (error) {
				this.comments = [];
				this.pagination = null;
				this.listError = apiErrorMessage(
					error,
					"Could not load comments.",
				);
			} finally {
				this.listLoading = false;
			}
		},
		goPage(page) {
			if (page < 1) {
				return;
			}
			this.fetchComments(page);
		},
		openAddModal() {
			this.submitError = null;
			this.draftComment = "";
			this.$bvModal.show(this.modalId);
		},
		onAddModalHidden() {
			this.draftComment = "";
			this.submitError = null;
		},
		async submitNewComment() {
			const ref = this.effectiveLoanRef;
			if (ref == null || !this.canSubmitComment) {
				return;
			}
			this.submitLoading = true;
			this.submitError = null;
			try {
				await ApiService.post(loanCommentsPath(ref), {
					comment: (this.draftComment || "").trim(),
				});
				this.$bvModal.hide(this.modalId);
				this.draftComment = "";
				await this.fetchComments(1);
			} catch (error) {
				this.submitError = apiErrorMessage(
					error,
					"Could not add comment.",
				);
			} finally {
				this.submitLoading = false;
			}
		},
	},
};
</script>
